import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DocumentSchema,
  DocumentTypeSchema,
  EmployeeLeaveBalancesSchema,
  LeaveDelegateSchema,
  LeaveRequestSchema,
  LeaveTypeSchema,
  WorkingDaysSchema,
  type EmployeeDto,
  type LeaveBalanceDto,
  type LeaveRequestDto,
  type ShortLeaveAllowanceDto,
} from '@taqon/contracts';
import { Link as RouterLink } from 'react-router';
import { z } from 'zod';
import {
  ApiError,
  getData,
  getPage,
  newIdempotencyKey,
  postForm,
  postJson,
} from '../../shared/api/client';
import { FileRules, oversizeMessage, useUploadLimit } from '../../shared/documents/upload-limit';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, Section } from '../../shared/ui/page';
import { EmployeePicker } from '../finance/lookups';
import { PersonName } from '../../shared/ui/person';

/**
 * Pieces the leave pages share (FRD-HRP-025 to HRP-031): the balances table, a page of requests,
 * the request dialog (submit or edit-and-resubmit) and the cancel action. Every decision is the
 * API's; the page only offers what the session's capabilities suggest it may do.
 */

export const LEAVE_REQUESTS = '/api/v1/hr/leave-requests';
const DOCUMENTS_PATH = '/api/v1/documents';
export const LEAVE_TYPES = '/api/v1/hr/leave-types';

export const leaveRequestPath = (id: string) => `/hr/leave/requests/${encodeURIComponent(id)}`;

/** Days as people read them: "2.50" → "2.5", "3.00" → "3". */
export function daysLabel(value: string): string {
  return value.includes('.') ? value.replace(/0+$/, '').replace(/\.$/, '') : value;
}

/**
 * A request's duration as people read it: "3 days", or "2.5 hours" for short leave, which counts
 * hours and no days (FRD-HRP-081). A converted request counts days like any other.
 */
export function durationLabel(request: {
  durationDays: string;
  durationHours?: string | null | undefined;
}): string {
  const { durationHours } = request;
  if (durationHours != null && Number(request.durationDays) === 0) {
    const hours = daysLabel(durationHours);
    return `${hours} ${hours === '1' ? 'hour' : 'hours'}`;
  }
  const days = daysLabel(request.durationDays);
  return `${days} ${days === '1' ? 'day' : 'days'}`;
}

/** The active leave types a request may name, readable by anyone who may view leave. */
export function useRequestableTypes() {
  return useQuery({
    queryKey: ['hr', 'leave', 'types'],
    queryFn: () => getData('/api/v1/hr/leave/types', z.array(LeaveTypeSchema)),
  });
}

/** An employee's balances, or the caller's own when `employeeId` is null. */
export function useBalances(
  employeeId: string | null,
  options: { asOf?: string | undefined; enabled?: boolean } = {},
) {
  const { asOf, enabled = true } = options;
  const query = asOf === undefined ? '' : `?asOf=${asOf}`;
  const path =
    employeeId === null
      ? `/api/v1/hr/leave/my-balances${query}`
      : `/api/v1/hr/employees/${employeeId}/leave-balances${query}`;
  return useQuery({
    queryKey: ['hr', 'leave', 'balances', employeeId ?? 'me', asOf ?? 'today'],
    enabled,
    queryFn: () => getData(path, EmployeeLeaveBalancesSchema),
  });
}

/**
 * Balances per tracked type, with the entitlement period each one is for (FRD-HRP-028).
 * "Available" is remaining less pending — what a new request is checked against. Short leave has
 * no balance: its hours used and left in the month are listed below the balances (FRD-HRP-081).
 */
export function BalancesTable({
  balances,
  shortLeave = [],
}: {
  balances: readonly LeaveBalanceDto[];
  shortLeave?: readonly ShortLeaveAllowanceDto[];
}) {
  if (balances.length === 0 && shortLeave.length === 0) {
    return (
      <EmptyState
        title="No leave balances"
        detail="Only leave types that track a balance have one."
      />
    );
  }
  return (
    <>
      {balances.length > 0 && <DayBalances balances={balances} />}
      {shortLeave.length > 0 && <ShortLeaveHours allowances={shortLeave} />}
    </>
  );
}

function ShortLeaveHours({ allowances }: { allowances: readonly ShortLeaveAllowanceDto[] }) {
  return (
    <Table size="small" aria-label="Short leave hours">
      <TableHead>
        <TableRow>
          <TableCell>Short leave</TableCell>
          <TableCell>Month</TableCell>
          <TableCell align="right">Monthly limit (hours)</TableCell>
          <TableCell align="right">Taken</TableCell>
          <TableCell align="right">Pending</TableCell>
          <TableCell align="right">Available</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {allowances.map((a) => (
          <TableRow key={a.leaveTypeId}>
            <TableCell>{a.leaveTypeName}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              {a.monthStart} to {a.monthEnd}
            </TableCell>
            <TableCell align="right">{daysLabel(a.limitHours)}</TableCell>
            <TableCell align="right">{daysLabel(a.taken)}</TableCell>
            <TableCell align="right">{daysLabel(a.pending)}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              {daysLabel(a.available)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function DayBalances({ balances }: { balances: readonly LeaveBalanceDto[] }) {
  return (
    <Table size="small" aria-label="Leave balances">
      <TableHead>
        <TableRow>
          <TableCell>Leave type</TableCell>
          <TableCell>Period</TableCell>
          <TableCell align="right">Entitlement</TableCell>
          <TableCell align="right">Carried forward</TableCell>
          <TableCell align="right">Taken</TableCell>
          <TableCell align="right">Pending</TableCell>
          <TableCell align="right">Remaining</TableCell>
          <TableCell align="right">Available</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {balances.map((b) => (
          <TableRow key={b.leaveTypeId}>
            <TableCell>{b.leaveTypeName}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              {b.periodStart === null
                ? 'Not yet employed'
                : `${b.periodStart} to ${b.periodEnd ?? ''}`}
            </TableCell>
            <TableCell align="right">{daysLabel(b.entitlement)}</TableCell>
            <TableCell align="right">{daysLabel(b.carriedForward)}</TableCell>
            <TableCell align="right">{daysLabel(b.taken)}</TableCell>
            <TableCell align="right">{daysLabel(b.pending)}</TableCell>
            <TableCell align="right">{daysLabel(b.remaining)}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              {daysLabel(b.available)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** A page of leave requests; approved leave is highlighted (FRD-HRP-031). */
export function LeaveRequestsTable({
  requests,
  showEmployee,
  onCancel,
}: {
  requests: readonly LeaveRequestDto[];
  showEmployee: boolean;
  onCancel?: ((request: LeaveRequestDto) => void) | undefined;
}) {
  const session = useSession();
  const canChange = hasCapability(session.data, 'hr.leave_request:change');
  return (
    <Table size="small" aria-label="Leave requests">
      <TableHead>
        <TableRow>
          <TableCell>Number</TableCell>
          {showEmployee && <TableCell>Employee</TableCell>}
          <TableCell>Leave type</TableCell>
          <TableCell>From</TableCell>
          <TableCell>To</TableCell>
          <TableCell align="right">Duration</TableCell>
          <TableCell>Status</TableCell>
          {onCancel !== undefined && <TableCell align="right">Action</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {requests.map((r) => (
          <TableRow
            key={r.id}
            hover
            sx={r.status === 'approved' ? { bgcolor: 'action.selected' } : undefined}
          >
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              <Link component={RouterLink} to={leaveRequestPath(r.id)} sx={{ fontWeight: 600 }}>
                {r.code}
              </Link>
            </TableCell>
            {showEmployee && (
              <TableCell>
                <PersonName name={r.employeeName} employeeId={r.employeeId}>
                  {r.employeeCode} — {r.employeeName}
                </PersonName>
              </TableCell>
            )}
            <TableCell>{r.leaveTypeName}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.startDate}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.endDate}</TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
              {durationLabel(r)}
            </TableCell>
            <TableCell>
              <StatusChip status={r.status} />
            </TableCell>
            {onCancel !== undefined && (
              <TableCell align="right">
                {mayCancel(r, session.data?.userId, canChange) && (
                  <Button
                    size="small"
                    color="warning"
                    aria-label={`Withdraw ${r.code}`}
                    onClick={() => {
                      onCancel(r);
                    }}
                  >
                    Withdraw
                  </Button>
                )}
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * Whether to offer Cancel: a request still in approval, to its requester or to someone who may
 * change leave requests — what the API allows (FRD-HRP-030). An employee whose leave HR
 * submitted on their behalf is not the requester, so it is not offered to them.
 */
export function mayCancel(
  request: Pick<LeaveRequestDto, 'status' | 'requestedBy'>,
  userId: string | undefined,
  canChange: boolean,
): boolean {
  return request.status === 'submitted' && (request.requestedBy === userId || canChange);
}

/** A paged, server-filtered list of leave requests. */
export function LeaveRequestList({
  filters,
  showEmployee,
  cancellable,
  emptyTitle,
  label,
}: {
  filters: Readonly<Record<string, string>>;
  showEmployee: boolean;
  cancellable: boolean;
  emptyTitle: string;
  label: string;
}) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [cancelling, setCancelling] = useState<LeaveRequestDto | null>(null);
  const params = new URLSearchParams({
    page: String(page + 1),
    pageSize: String(pageSize),
    sort: '-start_date',
  });
  for (const [key, value] of Object.entries(filters)) {
    if (value !== '') params.set(`filter[${key}]`, value);
  }
  const requests = useQuery({
    queryKey: ['hr', 'leave', 'requests', params.toString()],
    queryFn: () => getPage(`${LEAVE_REQUESTS}?${params.toString()}`, LeaveRequestSchema),
  });
  return (
    <>
      {requests.isPending && <LoadingState label={`Loading ${label}`} />}
      {requests.isError && <ErrorState message={requests.error.message} />}
      {requests.data?.items.length === 0 && <EmptyState title={emptyTitle} />}
      {requests.data !== undefined && requests.data.items.length > 0 && (
        <>
          <LeaveRequestsTable
            requests={requests.data.items}
            showEmployee={showEmployee}
            onCancel={cancellable ? setCancelling : undefined}
          />
          <TablePagination
            component="div"
            count={requests.data.total}
            page={page}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[25, 50, 100]}
            onPageChange={(_, next) => {
              setPage(next);
            }}
            onRowsPerPageChange={(e) => {
              setPageSize(Number.parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}
      {cancelling !== null && (
        <CancelLeaveDialog
          request={cancelling}
          onClose={() => {
            setCancelling(null);
          }}
        />
      )}
    </>
  );
}

/** Cancels a request in approval, after asking (FRD-HRP-030). */
export function CancelLeaveDialog({
  request,
  onClose,
}: {
  request: LeaveRequestDto;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const cancel = useMutation({
    mutationFn: () =>
      postJson(
        `${LEAVE_REQUESTS}/${request.id}/cancel`,
        {},
        LeaveRequestSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: request.version },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'leave'] });
      await queryClient.invalidateQueries({ queryKey: ['workflow'] });
      onClose();
    },
  });
  return (
    <ConfirmDialog
      open
      title={`Withdraw leave request ${request.code}?`}
      message={`The ${request.leaveTypeName.toLowerCase()} from ${request.startDate} to ${request.endDate} is withdrawn from approval. This cannot be undone.`}
      confirmLabel="Withdraw"
      tone="warning"
      busy={cancel.isPending}
      error={cancel.isError ? cancel.error.message : null}
      onCancel={onClose}
      onConfirm={() => {
        cancel.mutate();
      }}
    />
  );
}

const HALF_DAYS = /^\d{1,3}(\.[05])?$/;

/** Whether a request of `days` needs a reason and a supporting document (FRD-HRP-094). */
export function supportingRequired(threshold: string | null, days: number): boolean {
  return threshold !== null && days > Number(threshold);
}

/** The working days of a range, as the API computes them (CLR-46, FRD-HRP-090). */
function useWorkingDays(startDate: string, endDate: string, enabled: boolean) {
  return useQuery({
    queryKey: ['hr', 'leave', 'working-days', startDate, endDate],
    enabled,
    retry: false,
    queryFn: () =>
      getData(
        `/api/v1/hr/leave/working-days?startDate=${startDate}&endDate=${endDate}`,
        WorkingDaysSchema,
      ),
  });
}

/**
 * Submit a leave request, or edit a returned / rejected one and resubmit it (`editing`). The
 * duration of leave in days is computed by the API from the dates: working days only, without the
 * weekend and public holidays (CLR-46); a single working day may be taken as half a day. Above
 * the configured days the requester names a delegate; above its type's threshold the request needs
 * a reason and a supporting document, uploaded right after the request is submitted (approval is
 * refused until it has been scanned). The API decides overlap and balance.
 *
 * Short leave (FRD-HRP-081, HRP-082) is asked for in hours on one date. The dialog says before
 * submission when the hours are above the type's hours per request, because the request is then
 * recorded as the conversion leave type instead; the API decides the monthly limit.
 */
export function LeaveRequestDialog({
  employee,
  editing,
  onClose,
}: {
  /** Null: the caller's own record (self-service). Undefined: choose one with the picker. */
  employee: EmployeeDto | null | undefined;
  editing?: LeaveRequestDto;
  onClose: (saved?: LeaveRequestDto) => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const types = useRequestableTypes();
  const [chosen, setChosen] = useState<EmployeeDto | null>(null);
  const target = employee === undefined ? chosen : employee;
  // A converted request is edited as the short leave it was asked for.
  const [typeId, setTypeId] = useState(
    editing?.convertedFromLeaveTypeId ?? editing?.leaveTypeId ?? '',
  );
  const [startDate, setStartDate] = useState(editing?.startDate ?? '');
  const [endDate, setEndDate] = useState(editing?.endDate ?? '');
  const [halfDay, setHalfDay] = useState(
    editing !== undefined && editing.durationHours === null && editing.durationDays === '0.5',
  );
  const [delegateId, setDelegateId] = useState(editing?.delegateEmployeeId ?? '');
  const [file, setFile] = useState<File | null>(null);
  const limit = useUploadLimit();
  const [idempotencyKey] = useState(newIdempotencyKey);
  const [hours, setHours] = useState(
    editing?.durationHours == null ? '' : daysLabel(editing.durationHours),
  );
  const [reason, setReason] = useState(editing?.reason ?? '');
  const balanceOwner = editing?.employeeId ?? target?.id ?? null;
  // With the picker, balances are read once an employee is chosen.
  const balances = useBalances(balanceOwner, {
    enabled: editing !== undefined || employee !== undefined || chosen !== null,
  });
  const balanceFor = (id: string) => balances.data?.balances.find((b) => b.leaveTypeId === id);
  const allowanceFor = (id: string) => balances.data?.shortLeave.find((a) => a.leaveTypeId === id);
  const typeRows = types.data ?? [];
  const type = typeRows.find((t) => t.id === typeId);
  const inHours = type?.countedIn === 'hours';

  const datesOk = inHours
    ? startDate !== ''
    : startDate !== '' && endDate !== '' && endDate >= startDate;
  const calendar = useWorkingDays(startDate, endDate, !inHours && datesOk);
  const working = calendar.data?.workingDays;
  const days = working === undefined ? 0 : working === 1 && halfDay ? 0.5 : working;
  const durationOk = working !== undefined && working > 0;
  const durationHint = !datesOk
    ? 'Computed from the dates: weekends and public holidays are not counted'
    : calendar.isError
      ? messageOf(calendar.error)
      : working === undefined
        ? 'Counting the working days…'
        : working === 0
          ? 'These dates hold no working day'
          : [
              `${String(calendar.data?.weekendDays ?? 0)} weekend days`,
              ...(calendar.data?.holidays ?? []).map((h) => `${h.name} (${h.date})`),
            ].join(', ') + ' not counted';
  const delegateAbove = calendar.data?.delegateRequiredAboveDays;
  const needsDelegate = !inHours && delegateAbove !== undefined && days > delegateAbove;
  const delegates = useQuery({
    queryKey: ['hr', 'leave', 'delegates'],
    enabled: needsDelegate || delegateId !== '',
    queryFn: () => getData('/api/v1/hr/leave/delegates', z.array(LeaveDelegateSchema)),
  });
  const ownEmployeeId = editing?.employeeId ?? target?.id ?? balances.data?.employeeId ?? null;
  const dayHours = Number(type?.hoursPerDay ?? '0');
  const mostHours = Number(type?.maxHoursPerRequest ?? '0');
  const hoursOk = HALF_DAYS.test(hours) && Number(hours) > 0 && Number(hours) <= dayHours;
  const hoursHint =
    hours === ''
      ? `In half hours, e.g. 2 or 2.5; up to ${daysLabel(type?.maxHoursPerRequest ?? '')} hours`
      : !HALF_DAYS.test(hours) || Number(hours) <= 0
        ? 'Enter hours in half-hour steps, above zero'
        : Number(hours) > dayHours
          ? `At most the ${daysLabel(type?.hoursPerDay ?? '')} hours of a working day`
          : undefined;
  const converts = inHours && hoursOk && Number(hours) > mostHours;
  const conversionName =
    typeRows.find((t) => t.id === type?.conversionLeaveTypeId)?.name ?? 'another leave type';
  const convertedDays = Number(hours) * 2 <= dayHours ? 'half a day' : 'one day';
  // A longer short-leave request becomes the conversion type and follows its rule.
  const ruleType = converts ? typeRows.find((t) => t.id === type.conversionLeaveTypeId) : type;
  const requestedDays = inHours ? (converts ? (convertedDays === 'one day' ? 1 : 0.5) : 0) : days;
  const needsEvidence = supportingRequired(
    ruleType?.supportingRequiredAboveDays ?? null,
    requestedDays,
  );
  const documentTypes = useQuery({
    queryKey: ['document-types', 'hr.leave_request'],
    enabled: needsEvidence,
    queryFn: () =>
      getData('/api/v1/documents/types?recordType=hr.leave_request', z.array(DocumentTypeSchema)),
  });
  const documentType = documentTypes.data?.[0];
  const canSubmit =
    typeId !== '' &&
    datesOk &&
    (inHours ? hoursOk : durationOk) &&
    (!needsDelegate || delegateId !== '') &&
    (!needsEvidence || reason.trim() !== '') &&
    (!needsEvidence || editing !== undefined || documentType === undefined || file !== null) &&
    oversizeMessage(file, limit) === null &&
    (editing !== undefined || employee !== undefined || chosen !== null);

  const fields = {
    leaveTypeId: typeId,
    startDate,
    ...(inHours
      ? { endDate: startDate, durationHours: hours }
      : { endDate, ...(working === 1 && halfDay ? { durationDays: '0.5' } : {}) }),
    ...(reason.trim() === '' ? {} : { reason: reason.trim() }),
    ...(delegateId === '' || inHours ? {} : { delegateEmployeeId: delegateId }),
  };
  // The supporting document goes to the request just submitted (SDD §17.4: fields, then file).
  const attach = async (request: LeaveRequestDto) => {
    if (file === null || documentType === undefined) return;
    const form = new FormData();
    form.append('ownerRecordType', 'hr.leave_request');
    form.append('ownerRecordId', request.id);
    form.append('documentTypeCode', documentType.code);
    form.append('title', `Supporting document for ${request.code}`);
    form.append('file', file, file.name);
    try {
      await postForm(DOCUMENTS_PATH, form, DocumentSchema, session.data?.csrfToken ?? '', {
        idempotencyKey,
      });
    } catch {
      // The request stands; its page says a document is still needed and offers Upload.
    }
  };
  const save = useMutation({
    mutationFn: () =>
      editing === undefined
        ? postJson(
            LEAVE_REQUESTS,
            employee === undefined && chosen !== null
              ? { employeeId: chosen.id, ...fields }
              : fields,
            LeaveRequestSchema,
            session.data?.csrfToken ?? '',
          )
        : postJson(
            `${LEAVE_REQUESTS}/${editing.id}/resubmit`,
            fields,
            LeaveRequestSchema,
            session.data?.csrfToken ?? '',
            { expectedVersion: editing.version },
          ),
    onSuccess: async (saved) => {
      await attach(saved);
      await queryClient.invalidateQueries({ queryKey: ['hr', 'leave'] });
      await queryClient.invalidateQueries({ queryKey: ['documents'] });
      await queryClient.invalidateQueries({ queryKey: ['workflow'] });
      onClose(saved);
    },
  });
  const allowance = allowanceFor(typeId);

  return (
    <FormDialog
      open
      title={editing === undefined ? 'Request leave' : `Edit and resubmit ${editing.code}`}
      submitLabel={editing === undefined ? 'Submit request' : 'Resubmit'}
      canSubmit={canSubmit}
      submitting={save.isPending}
      error={save.isError ? messageOf(save.error) : null}
      onClose={() => {
        onClose();
      }}
      onSubmit={() => {
        save.mutate();
      }}
    >
      {employee === undefined && editing === undefined && (
        <EmployeePicker required value={chosen} onChange={setChosen} />
      )}
      {types.isError && <ErrorState message={types.error.message} />}
      <TextField
        select
        required
        label="Leave type"
        value={typeId}
        onChange={(e) => {
          setTypeId(e.target.value);
        }}
        helperText={
          typeRows.length === 0 && !types.isPending ? 'No leave type is configured' : undefined
        }
      >
        {typeRows.map((t) => {
          const balance = balanceFor(t.id);
          const left = allowanceFor(t.id);
          return (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
              {balance !== undefined && ` — ${daysLabel(balance.available)} days available`}
              {left !== undefined && ` — ${daysLabel(left.available)} hours left this month`}
              {t.balanceTracked || t.countedIn === 'hours' ? '' : ' (no balance)'}
            </MenuItem>
          );
        })}
      </TextField>
      {inHours ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            required
            type="date"
            label="Date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />
          <TextField
            required
            label="Duration (hours)"
            value={hours}
            error={hours !== '' && !hoursOk}
            helperText={hoursHint}
            onChange={(e) => {
              setHours(e.target.value.trim());
            }}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            fullWidth
          />
        </Stack>
      ) : (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              required
              type="date"
              label="From"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
              }}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
            <TextField
              required
              type="date"
              label="To"
              value={endDate}
              error={startDate !== '' && endDate !== '' && endDate < startDate}
              helperText={
                startDate !== '' && endDate !== '' && endDate < startDate
                  ? 'The end date cannot be before the start date'
                  : undefined
              }
              onChange={(e) => {
                setEndDate(e.target.value);
              }}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
          </Stack>
          <TextField
            label="Duration (working days)"
            value={working === undefined ? '' : daysLabel(String(days))}
            error={datesOk && (calendar.isError || working === 0)}
            helperText={durationHint}
            slotProps={{ htmlInput: { readOnly: true } }}
          />
          {working === 1 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={halfDay}
                  onChange={(e) => {
                    setHalfDay(e.target.checked);
                  }}
                />
              }
              label="Half day"
            />
          )}
          {needsDelegate && (
            <TextField
              select
              required
              label="Delegate"
              value={delegateId}
              helperText={`Leave of more than ${String(delegateAbove)} working days names the colleague who covers your work`}
              onChange={(e) => {
                setDelegateId(e.target.value);
              }}
            >
              {(delegates.data ?? [])
                .filter((d) => d.id !== ownEmployeeId)
                .map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name} ({d.code})
                  </MenuItem>
                ))}
            </TextField>
          )}
        </>
      )}
      <TextField
        label="Reason"
        required={needsEvidence}
        multiline
        minRows={2}
        value={reason}
        helperText={
          needsEvidence
            ? `${ruleType?.name ?? 'This leave'} needs a reason and a supporting document`
            : undefined
        }
        onChange={(e) => {
          setReason(e.target.value);
        }}
      />
      {needsEvidence && documentType !== undefined && (
        <Box>
          <Typography component="label" htmlFor="leave-supporting-file" variant="body2">
            Supporting document{editing === undefined ? ' *' : ''}
          </Typography>
          <Box
            component="input"
            id="leave-supporting-file"
            type="file"
            aria-label="Supporting document"
            aria-required={editing === undefined}
            accept={documentType.allowedContentTypes.join(',')}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFile(event.target.files?.[0] ?? null);
            }}
            sx={{ display: 'block', mt: 0.5, maxWidth: '100%' }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {editing === undefined
              ? 'Uploaded with the request. It cannot be approved until the document has been scanned.'
              : 'Add another document if needed; the request page lists those already attached.'}
          </Typography>
          <FileRules accepted={null} file={file} limit={limit} />
        </Box>
      )}
      {needsEvidence && documentTypes.data !== undefined && documentType === undefined && (
        <Alert severity="warning" variant="outlined">
          This leave needs a supporting document, but no document type is configured for leave
          requests yet: ask HR. The request cannot be approved until a document is attached.
        </Alert>
      )}
      {converts && (
        <Alert severity="warning" variant="outlined">
          More than {daysLabel(type.maxHoursPerRequest ?? '')} hours is not {type.name}: this
          request will be submitted as {conversionName} and use {convertedDays} of that balance.
        </Alert>
      )}
      {inHours && !converts && allowance !== undefined && (
        <Box>
          <Typography variant="body2" color="text.secondary">
            This month: {daysLabel(allowance.taken)} taken, {daysLabel(allowance.pending)} pending,{' '}
            {daysLabel(allowance.available)} of {daysLabel(allowance.limitHours)} hours left. The
            month of the date you choose is checked when you submit.
          </Typography>
        </Box>
      )}
      {!inHours && typeId !== '' && balanceFor(typeId) !== undefined && (
        <Box>
          <Typography variant="body2" color="text.secondary">
            Balance today: {daysLabel(balanceFor(typeId)?.remaining ?? '0')} remaining,{' '}
            {daysLabel(balanceFor(typeId)?.pending ?? '0')} pending,{' '}
            {daysLabel(balanceFor(typeId)?.available ?? '0')} available. The balance of the period
            the leave starts in is checked when you submit.
          </Typography>
        </Box>
      )}
    </FormDialog>
  );
}

/** The API's refusal, with its field messages (e.g. the available balance). */
export function messageOf(error: Error): string {
  if (error instanceof ApiError) {
    const errors = error.problem?.errors ?? [];
    if (errors.length > 0) return `${error.message}: ${errors.map((e) => e.message).join('; ')}`;
  }
  return error.message;
}

/**
 * The Leave section of an employee's page (FRD-HRP-031): balances and the employee's requests,
 * approved leave highlighted. Shown to a session that may view leave; the API decides the scope.
 */
export function EmployeeLeaveSection({ employeeId }: { employeeId: string }) {
  const session = useSession();
  const canView = hasCapability(session.data, 'hr.leave_request:view');
  if (!canView) return null;
  return <EmployeeLeave employeeId={employeeId} />;
}

function EmployeeLeave({ employeeId }: { employeeId: string }) {
  const balances = useBalances(employeeId);
  const notVisible = balances.error instanceof ApiError && balances.error.status === 404;
  if (notVisible) return null;
  return (
    <Section title="Leave">
      {balances.isPending && <LoadingState label="Loading leave balances" />}
      {balances.isError && <ErrorState message={balances.error.message} />}
      {balances.data !== undefined && (
        <Stack spacing={0}>
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1.5 }}>
            Balances as of {balances.data.asOf}
          </Typography>
          <BalancesTable balances={balances.data.balances} shortLeave={balances.data.shortLeave} />
          <Typography variant="subtitle2" component="h3" sx={{ px: 2, pt: 2 }}>
            Leave requests
          </Typography>
          <LeaveRequestList
            filters={{ employee_id: employeeId }}
            showEmployee={false}
            cancellable={false}
            emptyTitle="No leave requests"
            label="leave requests"
          />
        </Stack>
      )}
    </Section>
  );
}

/** Shown when the signed-in user has no employee record for self-service. */
export function NoEmployeeLink() {
  return (
    <Section>
      <EmptyState
        title="No employee record is linked to your user"
        detail="Ask your administrator to link your user to your employee record."
      />
    </Section>
  );
}

export function CreatedNotice({ code }: { code: string }) {
  return (
    <Alert severity="success" variant="outlined">
      {code} submitted for approval.
    </Alert>
  );
}
